import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { Tab, Tabs } from '@mui/material';
import { ConfigContext } from 'components/ConfigGuard';
import ExpenseReportCollectionSection from 'components/expense/ExpenseReportCollectionPage/ExpenseReportCollectionSection';
import { ParametersContext } from 'components/ParametersGuard';
import { useHistory, useLocation } from 'react-router';
import routes from 'store/configs/Routes';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';
import styles from 'components/expense/Expense.module.scss';

const tabConfig = {
  ExpenseReport: { key: 'ExpenseReport', label: 'My Reimbursements', route: routes.expenseReport } as TabItem,
  ExpenseReportOtherPerson: {
    key: 'ExpenseReportOtherPerson',
    label: 'Authorized Reimbursements',
    route: routes.expenseReportOtherPerson,
  } as TabItem,
};

const ExpenseReportCollectionPage: React.FunctionComponent = () => {
  const {
    modulesSettings: {
      expenseReport: { hiddenTabs, reportsTitle },
    },
  } = useContext(ConfigContext);

  const {
    expenseReport: { reportsPageHeader, reportsTabHeader, reportsDelegationTabHeader },
  } = useContext(ParametersContext);

  const history = useHistory();
  const { pathname } = useLocation();

  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.ExpenseReport.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, visibleTabs]);

  return (
    <>
      <div className={styles.contentBlock}>
        {reportsTitle && (
          <div className={styles.contentBlock}>
            <h1 className={commonStyles.pageTitle}>{reportsTitle}</h1>
          </div>
        )}
        {reportsPageHeader && (
          <div className={styles.contentBlock} dangerouslySetInnerHTML={{ __html: reportsPageHeader }} />
        )}
      </div>

      <TabContext value={activeTab}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor={'primary'}
          textColor={'primary'}
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
            },
          }}
        >
          {visibleTabs.map((tab) => {
            return (
              <Tab
                key={tab.key}
                value={tab.key}
                label={tab.label}
                className={commonStyles.tab}
                classes={{ selected: commonStyles.activeTab }}
              />
            );
          })}
        </Tabs>

        <TabPanel value={tabConfig.ExpenseReport.key} className={commonStyles.tabPanel}>
          {reportsTabHeader && (
            <div className={styles.contentBlock} dangerouslySetInnerHTML={{ __html: reportsTabHeader }} />
          )}

          <ExpenseReportCollectionSection isDelegationTabSelected={false} />
        </TabPanel>

        <TabPanel value={tabConfig.ExpenseReportOtherPerson.key} className={commonStyles.tabPanel}>
          {reportsDelegationTabHeader && (
            <div className={styles.contentBlock} dangerouslySetInnerHTML={{ __html: reportsDelegationTabHeader }} />
          )}

          <ExpenseReportCollectionSection isDelegationTabSelected={true} />
        </TabPanel>
      </TabContext>
    </>
  );
};

export default ExpenseReportCollectionPage;
